import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, CheckCircle2, Globe, Search, TrendingUp, 
  Target, Zap, BarChart3, Users, Phone, ChevronRight,
  Award, Shield, Clock, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Questions for the intake flow
const QUESTIONS = [
  {
    id: 'business_name',
    label: "What's your business name?",
    type: 'text',
    placeholder: 'e.g. Vegas Eats Downtown',
  },
  {
    id: 'website_url',
    label: "What's your website URL? (leave blank if none)",
    type: 'text',
    placeholder: "e.g. www.yourbusiness.com or 'No website yet'",
  },
  {
    id: 'niche',
    label: 'What type of business are you?',
    type: 'select',
    options: [
      'Restaurant / Bar / Hospitality',
      'Medical / MedSpa / Wellness',
      'Real Estate',
      'Law Firm / Legal',
      'Contractor / Trades',
      'Beauty / Salon / Spa',
      'Retail / E-Commerce',
      'Fitness / Gym',
      'Church / Nonprofit',
      'Consulting / Professional Services',
      'Other',
    ],
  },
  {
    id: 'goal',
    label: "What's your #1 growth goal right now?",
    type: 'select',
    options: [
      'Get more customers finding me online',
      'Look more professional than my competitors',
      'Turn my website into a lead machine',
      'Launch my online presence from scratch',
      'Automate bookings / inquiries',
    ],
  },
  {
    id: 'monthly_revenue',
    label: "Roughly what's your current monthly revenue?",
    type: 'select',
    options: [
      'Just starting out / pre-revenue',
      'Under $10K/month',
      '$10K - $50K/month',
      '$50K - $100K/month',
      '$100K+/month',
    ],
  },
  {
    id: 'biggest_pain',
    label: "What's your biggest digital frustration right now?",
    type: 'select',
    options: [
      'Nobody can find me on Google',
      'My website looks outdated or embarrassing',
      'I have no idea if my site is even working',
      'Competitors are beating me online',
      'I have no website at all',
    ],
  },
  {
    id: 'email',
    label: 'Where should we send your Growth Plan?',
    type: 'email',
    placeholder: 'your@email.com',
  },
];

// Analyzing checklist steps
const ANALYSIS_STEPS = [
  'Scanning website structure...',
  'Analyzing Google presence...',
  'Researching Las Vegas competitors...',
  'Identifying SEO gaps...',
  'Calculating conversion score...',
  'Building your Growth Roadmap...',
  'Finalizing your report...',
];

// Scoring engine
function generateAuditReport(answers) {
  const hasWebsite =
    answers.website_url &&
    answers.website_url.toLowerCase() !== 'no website yet' &&
    answers.website_url.trim() !== '';

  const nicheMultipliers = {
    'Medical / MedSpa / Wellness': 1.25,
    'Restaurant / Bar / Hospitality': 1.2,
    'Real Estate': 1.15,
    'Law Firm / Legal': 1.1,
    'Contractor / Trades': 1.1,
    'Beauty / Salon / Spa': 1.05,
  };
  const multiplier = nicheMultipliers[answers.niche] || 1.0;

  let websiteScore = hasWebsite ? 52 : 18;
  let seoScore = hasWebsite ? 38 : 5;
  let conversionScore = hasWebsite ? 44 : 10;
  let competitorScore = Math.floor(55 + Math.random() * 20);

  const painPenalties = {
    'Nobody can find me on Google': { seo: -15 },
    'My website looks outdated or embarrassing': { website: -12 },
    'I have no idea if my site is even working': { conversion: -18 },
    'Competitors are beating me online': { competitor: -10 },
    'I have no website at all': { website: -35, seo: -30 },
  };
  const penalties = painPenalties[answers.biggest_pain] || {};
  websiteScore = Math.max(5, websiteScore + (penalties.website || 0));
  seoScore = Math.max(5, seoScore + (penalties.seo || 0));
  conversionScore = Math.max(5, conversionScore + (penalties.conversion || 0));
  competitorScore = Math.max(20, competitorScore + (penalties.competitor || 0));

  const overall = Math.floor(
    ((websiteScore + seoScore + conversionScore) / 3) * multiplier
  );

  const nicheCompetitors = {
    'Restaurant / Bar / Hospitality': ['Yelp-optimized chains', 'DoorDash-integrated spots'],
    'Medical / MedSpa / Wellness': ['Zocdoc-listed clinics', 'RealSelf-verified providers'],
    'Real Estate': ['Zillow Premier agents', 'RE/MAX franchises'],
    'Law Firm / Legal': ['Martindale-listed firms', 'Avvo-ranked attorneys'],
    'Contractor / Trades': ['HomeAdvisor Pro members', 'Angi-verified contractors'],
  };
  const competitors =
    nicheCompetitors[answers.niche] || ['Top Google-ranked competitors', 'Well-funded local players'];

  const seoGaps = [];
  if (!hasWebsite) seoGaps.push('No website = invisible on Google entirely');
  if (seoScore < 40) seoGaps.push('Missing Google Business Profile optimization');
  seoGaps.push(`No '${answers.niche} Las Vegas' keyword targeting found`);
  seoGaps.push('Zero backlinks from local Las Vegas directories');
  if (seoScore < 50) seoGaps.push('No schema markup for local business visibility');

  const conversionFixes = [];
  if (!hasWebsite) {
    conversionFixes.push('Launch a professional website (you have 0% digital conversion rate)');
    conversionFixes.push('Add click-to-call button (mobile users convert 3x more with one tap)');
    conversionFixes.push('Create a Google Business Profile to capture map searches');
  } else {
    conversionFixes.push('Add a primary CTA above the fold (most visitors leave in 8 seconds)');
    conversionFixes.push("Mobile page speed is likely below Google's 3-second threshold");
    conversionFixes.push('No trust signals (reviews, certifications, client count) visible');
    if (answers.goal === 'Automate bookings / inquiries') {
      conversionFixes.push('Add an online booking or inquiry form (reduces friction by 60%)');
    }
  }

  const roadmapSteps = [
    {
      week: 'Week 1-2',
      action: hasWebsite
        ? 'Website audit + competitor teardown + quick-win fixes'
        : 'Launch Express Website with Google Business Profile setup',
      impact: 'HIGH',
    },
    {
      week: 'Week 3-4',
      action: 'On-page SEO optimization + Las Vegas keyword targeting',
      impact: 'HIGH',
    },
    {
      week: 'Month 2',
      action: 'Conversion rate optimization (CTAs, trust signals, mobile speed)',
      impact: 'MEDIUM',
    },
    {
      week: 'Month 3',
      action: 'Local citation building + review strategy launch',
      impact: 'MEDIUM',
    },
    {
      week: 'Month 4+',
      action: 'Content marketing + monthly performance reporting',
      impact: 'ONGOING',
    },
  ];

  return {
    overall: Math.min(95, overall),
    websiteScore,
    seoScore,
    conversionScore,
    competitorScore,
    hasWebsite,
    competitors,
    seoGaps,
    conversionFixes,
    roadmapSteps,
    estimatedRevenueLift: hasWebsite ? '$3,000 - $12,000' : '$8,000 - $25,000',
  };
}

// Score Gauge Component
const ScoreGauge = ({ score, label, color, delay = 0 }) => {
  const [displayScore, setDisplayScore] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      let start = 0;
      const counter = setInterval(() => {
        start += 2;
        if (start >= score) {
          setDisplayScore(score);
          clearInterval(counter);
        } else {
          setDisplayScore(start);
        }
      }, 18);
      return () => clearInterval(counter);
    }, delay);
    return () => clearTimeout(timer);
  }, [score, delay]);

  const circumference = 2 * Math.PI * 36;
  const offset = circumference - (displayScore / 100) * circumference;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative w-[88px] h-[88px]">
        <svg width="88" height="88" viewBox="0 0 88 88" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx="44" cy="44" r="36" fill="none" stroke="#E5E7EB" strokeWidth="6" />
          <circle
            cx="44" cy="44" r="36" fill="none"
            stroke={color} strokeWidth="6"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.05s linear' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xl font-black" style={{ color }}>{displayScore}</span>
          <span className="text-[9px] text-slate-gray">/ 100</span>
        </div>
      </div>
      <span className="text-[10px] text-slate-gray tracking-widest uppercase text-center">{label}</span>
    </div>
  );
};

// Primary Button matching site style
const PrimaryButton = ({ children, className = "", ...props }) => (
  <Button
    className={`bg-gradient-to-r from-[#6B46C1] to-[#4169E1] text-white px-8 py-3.5 rounded-lg font-semibold shadow-lg hover:from-[#5534A5] hover:to-[#3152B8] hover:-translate-y-0.5 hover:shadow-xl transition-all duration-300 ${className}`}
    {...props}
  >
    {children}
  </Button>
);

const GrowthAuditPage = () => {
  const [stage, setStage] = useState('landing');
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [currentInput, setCurrentInput] = useState('');
  const [analysisStep, setAnalysisStep] = useState(0);
  const [report, setReport] = useState(null);
  const [ctaClicked, setCtaClicked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');
  const inputRef = useRef(null);

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Submit Growth Audit and send PDF via email
  const handleSubmitAudit = async () => {
    if (!report || isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/growth-audit/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          answers,
          report
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setCtaClicked(true);
        toast.success('Your Growth Plan has been sent to your email!');
      } else {
        toast.error(data.detail || 'Failed to send your Growth Plan. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting growth audit:', error);
      toast.error('Failed to send your Growth Plan. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Focus input when question changes
  useEffect(() => {
    if (stage === 'intake' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [currentQ, stage]);

  // Scroll to top when stage or question changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [stage, currentQ]);

  // Clear email error when input changes
  useEffect(() => {
    setEmailError('');
  }, [currentInput]);

  // Handle analyzing animation
  useEffect(() => {
    if (stage !== 'analyzing') return;

    if (analysisStep < ANALYSIS_STEPS.length - 1) {
      const timer = setTimeout(() => setAnalysisStep(s => s + 1), 900);
      return () => clearTimeout(timer);
    } else {
      // Generate report after all steps complete
      setTimeout(() => {
        setReport(generateAuditReport(answers));
        setStage('report');
      }, 900);
    }
  }, [stage, analysisStep, answers]);

  const handleStart = () => setStage('intake');

  const handleAnswer = () => {
    const q = QUESTIONS[currentQ];
    if (!currentInput.trim() && q.type !== 'select') return;
    
    // Validate email if this is the email question
    if (q.type === 'email') {
      if (!isValidEmail(currentInput.trim())) {
        setEmailError('Please enter a valid email address');
        return;
      }
    }
    
    const value = currentInput || answers[q.id] || (q.options ? q.options[0] : '');
    const newAnswers = { ...answers, [q.id]: value };
    setAnswers(newAnswers);
    setCurrentInput('');
    setEmailError('');

    if (currentQ < QUESTIONS.length - 1) {
      setCurrentQ(currentQ + 1);
    } else {
      setStage('analyzing');
    }
  };

  const handleSelectAnswer = (option) => {
    const newAnswers = { ...answers, [QUESTIONS[currentQ].id]: option };
    setAnswers(newAnswers);
    setCurrentInput('');
    if (currentQ < QUESTIONS.length - 1) {
      setCurrentQ(currentQ + 1);
    } else {
      setStage('analyzing');
    }
  };

  const progress = (currentQ / QUESTIONS.length) * 100;

  // Render Landing Stage
  const renderLanding = () => (
    <div className="animate-fadeUp">
      <div className="text-center max-w-4xl mx-auto">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-electric-indigo/10 border border-electric-indigo/25 rounded-full mb-6">
          <span className="w-1.5 h-1.5 rounded-full bg-electric-indigo shadow-[0_0_8px_#4169E1]" />
          <span className="text-xs text-electric-indigo font-bold tracking-widest uppercase">FREE • INSTANT RESULTS</span>
        </div>
        
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-carbon-black mb-4 leading-tight">
          Fearless
          <span className="text-gradient bg-gradient-to-r from-[#6B46C1] to-[#4169E1] bg-clip-text text-transparent block">Growth Audit</span>
        </h1>
        
        <p className="text-lg sm:text-xl text-slate-gray mb-10 max-w-xl mx-auto leading-relaxed">
          Get your free <strong className="text-carbon-black font-bold">Business Website Growth Plan</strong> in 60 seconds, tailored to your Las Vegas market.
        </p>

        {/* Feature Grid */}
        <div className="grid grid-cols-2 gap-3 max-w-lg mx-auto mb-10">
          {[
            { icon: '◈', label: 'Website Score', desc: 'See exactly where you stand' },
            { icon: '⚡', label: 'Competitor Analysis', desc: 'How you rank vs local rivals' },
            { icon: '◎', label: 'SEO Gap Report', desc: "Keywords you're missing" },
            { icon: '◇', label: 'Conversion Fixes', desc: 'Turn visitors into customers' },
          ].map((item, i) => (
            <div key={i} className="bg-white border border-cool-gray rounded-xl p-4 hover:border-electric-indigo/50 hover:shadow-lg transition-all text-left">
              <div className="flex items-start gap-3">
                <span className="text-lg text-electric-indigo">{item.icon}</span>
                <div>
                  <h3 className="font-bold text-carbon-black text-sm">{item.label}</h3>
                  <p className="text-xs text-slate-gray">{item.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <PrimaryButton onClick={handleStart} className="group text-lg px-10 py-5 shadow-lg hover:shadow-xl transition-shadow">
          Get Your Free Growth Plan
          <ArrowRight className="w-6 h-6 ml-2 inline group-hover:translate-x-1 transition-transform" />
        </PrimaryButton>

        <p className="text-xs text-slate-gray tracking-wider mt-4 uppercase">
          NO CREDIT CARD • NO SPAM • RESULTS IN 60 SECONDS
        </p>

        {/* Social Proof */}
        <div className="flex flex-wrap items-center justify-center gap-6 mt-10 text-xs text-slate-gray">
          {['Duda Certified', 'PMP Certified', 'Data Privacy Partner'].map(badge => (
            <span key={badge} className="border-l-2 border-electric-indigo/30 pl-3 tracking-wider uppercase">{badge}</span>
          ))}
        </div>
      </div>
    </div>
  );

  // Render Intake Stage
  const renderIntake = () => {
    const question = QUESTIONS[currentQ];
    
    return (
      <div className="animate-fadeUp max-w-2xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-10">
          <div className="flex justify-between text-xs mb-2">
            <span className="text-slate-gray tracking-widest uppercase">QUESTION {currentQ + 1} OF {QUESTIONS.length}</span>
            <span className="text-electric-indigo font-mono">{Math.round(progress)}% COMPLETE</span>
          </div>
          <div className="h-1 bg-cool-gray rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[#6B46C1] to-[#4169E1] rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Question */}
        <div key={currentQ} className="animate-fadeUp">
          <div className="text-xs text-electric-indigo tracking-widest uppercase font-mono mb-2">
            FEARLESS SCOUT ASKING
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-8 leading-tight">
            {question.label}
          </h2>

          {question.type === 'select' ? (
            <div className="flex flex-col gap-3">
              {question.options.map((option, i) => (
                <button
                  key={i}
                  onClick={() => handleSelectAnswer(option)}
                  className={`w-full text-left px-5 py-4 rounded-xl border transition-all ${
                    answers[question.id] === option
                      ? 'bg-electric-indigo/10 border-electric-indigo text-carbon-black'
                      : 'bg-white border-cool-gray text-slate-gray hover:border-electric-indigo/50 hover:bg-gray-50 hover:text-carbon-black'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div>
                <input
                  ref={inputRef}
                  type={question.type === 'email' ? 'text' : question.type}
                  placeholder={question.placeholder}
                  value={currentInput}
                  onChange={(e) => setCurrentInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAnswer()}
                  className={`w-full px-5 py-4 bg-white border rounded-xl text-carbon-black placeholder-slate-gray focus:ring-2 outline-none transition-all text-base ${
                    emailError 
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' 
                      : 'border-cool-gray focus:border-electric-indigo focus:ring-electric-indigo/20'
                  }`}
                />
                {emailError && (
                  <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                    <span>⚠️</span> {emailError}
                  </p>
                )}
              </div>
              <PrimaryButton onClick={handleAnswer} className="self-start">
                Continue
                <ChevronRight className="w-5 h-5 ml-1 inline" />
              </PrimaryButton>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render Analyzing Stage
  const renderAnalyzing = () => (
    <div className="animate-fadeUp max-w-xl mx-auto text-center py-12">
      {/* Spinner */}
      <div className="relative w-20 h-20 mx-auto mb-8">
        <div className="absolute inset-0 border-[3px] border-cool-gray rounded-full" />
        <div 
          className="absolute inset-0 border-[3px] border-electric-indigo rounded-full border-t-transparent animate-spin"
          style={{ boxShadow: '0 0 30px rgba(65,105,225,0.3)' }}
        />
      </div>

      <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-2 tracking-wide uppercase">
        Analyzing {(answers.business_name || 'Your Business').toUpperCase()}
      </h2>
      <p className="text-slate-gray text-sm mb-10">Fearless Scout is running your audit...</p>

      {/* Checklist */}
      <div className="text-left max-w-sm mx-auto space-y-0">
        {ANALYSIS_STEPS.map((step, i) => (
          <div 
            key={i}
            className={`flex items-center gap-3 py-3 border-b border-gray-100 last:border-0 transition-opacity duration-300 ${
              i <= analysisStep ? 'opacity-100' : 'opacity-20'
            }`}
          >
            <div className={`w-[18px] h-[18px] rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-black ${
              i < analysisStep ? 'bg-electric-indigo text-white' : 
              i === analysisStep ? 'border-2 border-electric-indigo animate-pulse' : 
              'bg-gray-100'
            }`}>
              {i < analysisStep ? '✓' : ''}
            </div>
            <span className={`text-sm font-mono ${i <= analysisStep ? 'text-carbon-black' : 'text-slate-gray'}`}>
              {step}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  // Render Report Stage
  const renderReport = () => {
    if (!report) return null;

    return (
      <div className="animate-fadeUp max-w-4xl mx-auto py-8">
        {/* Header */}
        <div className="mb-1 text-xs text-electric-indigo tracking-widest uppercase font-mono">
          YOUR FEARLESS GROWTH PLAN
        </div>
        <h2 className="text-3xl sm:text-4xl font-bold text-carbon-black mb-1">
          {answers.business_name || 'Your Business'} - Website Audit
        </h2>
        <p className="text-slate-gray text-sm mb-8">Las Vegas, NV Market • Generated by Fearless Scout AI</p>

        {/* Overall Score */}
        <div className="bg-gradient-to-r from-electric-indigo/5 to-deep-violet/5 border border-electric-indigo/20 rounded-2xl p-6 sm:p-8 mb-6">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <div className="text-xs tracking-widest text-slate-gray uppercase mb-2">OVERALL DIGITAL SCORE</div>
              <div className="text-6xl sm:text-7xl font-black" style={{ color: report.overall >= 60 ? '#F59E0B' : '#EF4444' }}>
                {report.overall}<span className="text-2xl text-slate-gray">/100</span>
              </div>
              <div className="text-sm text-slate-gray mt-2">
                {report.overall < 40 ? '⚠️ Serious gaps: competitors are winning your customers' :
                  report.overall < 60 ? '📊 Below average: significant room to grow' :
                    '📈 Average: good foundation, optimization needed'}
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-6 sm:gap-8">
              <ScoreGauge score={report.websiteScore} label="Website" color="#4169E1" delay={200} />
              <ScoreGauge score={report.seoScore} label="SEO" color="#6B46C1" delay={400} />
              <ScoreGauge score={report.conversionScore} label="Conversion" color="#EF4444" delay={600} />
              <ScoreGauge score={report.competitorScore} label="Competitors" color="#F59E0B" delay={800} />
            </div>
          </div>
        </div>

        {/* Two Column Grid */}
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          {/* SEO Gaps */}
          <div className="bg-white border border-purple-200 rounded-2xl p-5">
            <div className="text-xs tracking-widest text-[#6B46C1] uppercase font-bold mb-4 flex items-center gap-2">
              <Search className="w-4 h-4" /> SEO GAPS FOUND
            </div>
            <div className="space-y-3">
              {report.seoGaps.map((gap, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <span className="text-red-500 text-sm mt-0.5 flex-shrink-0">✗</span>
                  <span className="text-sm text-slate-gray leading-relaxed">{gap}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Competitor Comparison */}
          <div className="bg-white border border-amber-200 rounded-2xl p-5">
            <div className="text-xs tracking-widest text-amber-600 uppercase font-bold mb-4 flex items-center gap-2">
              <Users className="w-4 h-4" /> COMPETITOR COMPARISON
            </div>
            <p className="text-sm text-slate-gray mb-3">Top {answers.niche} competitors outranking you:</p>
            <div className="space-y-2 mb-4">
              {report.competitors.map((c, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                  <span className="text-sm text-slate-gray">{c}</span>
                  <span className="text-xs text-red-500 font-bold font-mono">AHEAD</span>
                </div>
              ))}
            </div>
            <div className="text-sm text-slate-gray">
              Estimated monthly revenue gap: <strong className="text-amber-600">{report.estimatedRevenueLift}</strong>
            </div>
          </div>
        </div>

        {/* Conversion Fixes */}
        <div className="bg-white border border-red-200 rounded-2xl p-5 mb-4">
          <div className="text-xs tracking-widest text-red-500 uppercase font-bold mb-4 flex items-center gap-2">
            <Target className="w-4 h-4" /> CONVERSION FIXES NEEDED
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {report.conversionFixes.map((fix, i) => (
              <div key={i} className="flex gap-3 items-start">
                <span className="text-red-500 text-sm flex-shrink-0">↗</span>
                <span className="text-sm text-slate-gray leading-relaxed">{fix}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Growth Roadmap */}
        <div className="bg-white border border-blue-200 rounded-2xl p-5 mb-8">
          <div className="text-xs tracking-widest text-electric-indigo uppercase font-bold mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" /> YOUR GROWTH ROADMAP
          </div>
          <div className="space-y-4">
            {report.roadmapSteps.map((step, i) => (
              <div key={i} className="flex gap-4 items-start">
                <div className="min-w-[80px] text-xs text-electric-indigo font-mono pt-0.5">{step.week}</div>
                <div className="flex-1 text-sm text-slate-gray leading-relaxed">{step.action}</div>
                <div className={`text-[9px] tracking-wider font-bold px-2 py-1 rounded flex-shrink-0 ${
                  step.impact === 'HIGH' ? 'bg-red-100 text-red-600' : 
                  step.impact === 'MEDIUM' ? 'bg-amber-100 text-amber-600' : 
                  'bg-blue-100 text-electric-indigo'
                }`}>
                  {step.impact}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Box */}
        <div className="bg-gradient-to-r from-electric-indigo/10 to-deep-violet/10 border border-electric-indigo/30 rounded-2xl p-8 text-center shadow-lg">
          <h3 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-3">
            Want us to implement this for you?
          </h3>
          <p className="text-slate-gray max-w-lg mx-auto mb-6 leading-relaxed">
            Your Growth Plan is ready. Fearless IT can execute every item on this roadmap from a new website to full SEO optimization starting this week.
          </p>

          {!ctaClicked ? (
            <div className="flex justify-center">
              <PrimaryButton 
                onClick={handleSubmitAudit} 
                className="text-lg px-12 py-5"
                disabled={isSubmitting}
                data-testid="submit-growth-audit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin inline" />
                    Sending Your Plan...
                  </>
                ) : (
                  'Send My Website Growth Plan'
                )}
              </PrimaryButton>
            </div>
          ) : (
            <div className="animate-fadeUp">
              <div className="text-4xl mb-3">🎉</div>
              <p className="text-electric-indigo font-bold text-lg mb-2">
                A member from our team will reach out within 24 hours.
              </p>
              <p className="text-sm text-slate-gray">
                Check your email at <strong className="text-carbon-black">{answers.email}</strong> for your full Growth Plan PDF.
              </p>
              <p className="text-sm text-slate-gray mt-3">
                Or text/call directly: <a href="tel:7027271773" className="text-electric-indigo font-medium">(702) 727-1773</a>
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-xs text-slate-gray tracking-widest uppercase opacity-50">
          FEARLESS IT • LAS VEGAS, NV • FEARLESSIT.IO
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-cloud-white">
      <SEO 
        title="Free Growth Audit | Fearless IT"
        description="Get a free personalized growth audit revealing exactly what's holding your website back and a step-by-step roadmap to outrank your competitors."
        keywords="growth audit, SEO audit, website analysis, competitor analysis, conversion optimization, free website audit"
        url="/growth-audit"
        image="https://static.prod-images.emergentagent.com/jobs/1c71d773-84d6-4251-9bae-60816a9265db/images/94ee626b5d5f402bd67ee08d57375cf53e4cfcf69d64e48ffd8223f073ed5d74.png"
      />
      
      <MarketingHeader />

      <main className="px-4 sm:px-6 py-16 sm:py-24">
        <div className="max-w-6xl mx-auto">
          {stage === 'landing' && renderLanding()}
          {stage === 'intake' && renderIntake()}
          {stage === 'analyzing' && renderAnalyzing()}
          {stage === 'report' && renderReport()}
        </div>
      </main>

      <MarketingFooter />

      {/* Custom animations */}
      <style>{`
        @keyframes fadeUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeUp {
          animation: fadeUp 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default GrowthAuditPage;
